<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="$router.push({ name: 'inventory-history' })">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Nouvel inventaire</h1>
          <p class="text-xs text-gray-400">Sélectionnez les produits à inventorier</p>
        </div>
      </div>
      <button
        class="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        :disabled="isCreating || selectedProducts.length === 0"
        @click="showConfirmModal = true">
        <i class="fas fa-check text-[10px]"></i>
        Créer l'inventaire
      </button>
    </div>

    <!-- Actions rapides -->
    <div class="flex flex-wrap gap-2 mb-5">
      <button @click="selectAllProducts"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
        :class="quickAction === 'all' ? 'bg-primary text-white border-primary' : 'text-gray-600 border-gray-200 bg-white hover:border-primary hover:text-primary'">
        <i class="fas fa-check-double text-[10px]"></i>
        Inventaire complet
      </button>
      <button @click="selectByCategory"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
        :class="quickAction === 'category' ? 'bg-primary text-white border-primary' : 'text-gray-600 border-gray-200 bg-white hover:border-primary hover:text-primary'">
        <i class="fas fa-folder text-[10px]"></i>
        Par catégorie
      </button>
      <button @click="selectLowStock"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
        :class="quickAction === 'low' ? 'bg-amber-500 text-white border-amber-500' : 'text-gray-600 border-gray-200 bg-white hover:border-amber-400 hover:text-amber-600'">
        <i class="fas fa-exclamation-triangle text-[10px]"></i>
        Stock faible
      </button>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Sélection catégories (inline) -->
        <div v-if="showCategorySelection" class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <i class="fas fa-folder text-primary text-xs"></i>
              <h2 class="text-sm font-semibold text-gray-700">Sélectionner les catégories</h2>
            </div>
            <button @click="showCategorySelection = false" class="text-gray-400 hover:text-gray-600 transition-colors">
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>

          <div class="p-4">
            <!-- Recherche catégories -->
            <div class="relative mb-3">
              <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
              <input v-model="categorySearch" @input="handleCategorySearch" type="text" placeholder="Rechercher..."
                class="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" />
            </div>

            <!-- Grille catégories -->
            <div class="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
              <div v-for="category in categories" :key="category.id"
                @click="toggleCategory(category.id)"
                class="flex items-center gap-2.5 px-3 py-2.5 border rounded-lg cursor-pointer transition-all"
                :class="selectedCategories.includes(category.id)
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'">
                <div class="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                  :class="selectedCategories.includes(category.id) ? 'bg-primary/10' : 'bg-gray-50'">
                  <i class="fas fa-folder text-[10px]"
                    :class="selectedCategories.includes(category.id) ? 'text-primary' : 'text-gray-400'"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium truncate">{{ category.name }}</p>
                  <p class="text-[10px] text-gray-400">{{ category.product_count || 0 }} produits</p>
                </div>
                <i v-if="selectedCategories.includes(category.id)" class="fas fa-check text-[10px] text-primary"></i>
              </div>
            </div>

            <button @click="loadProductsByCategories" :disabled="selectedCategories.length === 0"
              class="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 transition-colors">
              <i class="fas fa-arrow-right text-[10px]"></i>
              Continuer avec {{ selectedCategories.length }} catégorie(s)
            </button>
          </div>
        </div>

        <!-- Produits -->
        <div class="bg-white rounded-xl border border-gray-200">
          <!-- Filtres -->
          <div class="px-5 py-3 border-b border-gray-100 flex flex-wrap gap-3 items-center">
            <div class="flex-1 min-w-[200px] relative">
              <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
              <input v-model="productSearch" @input="handleProductSearch" type="text" placeholder="Rechercher par nom ou SKU..."
                class="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" />
            </div>

            <div class="relative w-36">
              <select v-model="stockFilter" @change="applyFilters"
                class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none">
                <option value="">Tous stocks</option>
                <option value="in-stock">En stock</option>
                <option value="low-stock">Stock faible</option>
                <option value="out-of-stock">Rupture</option>
              </select>
              <i class="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none"></i>
            </div>

            <div class="relative w-40">
              <select v-model="selectedCategoryFilter" @change="applyFilters"
                class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none">
                <option value="">Toutes catégories</option>
                <option v-for="category in allCategories" :key="category.id" :value="category.id">
                  {{ category.name }}
                </option>
              </select>
              <i class="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none"></i>
            </div>
          </div>

          <!-- Barre de sélection -->
          <div class="px-5 py-2 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
            <span class="text-xs font-medium text-primary">
              {{ selectedProducts.length }} / {{ totalProducts }} sélectionnés
            </span>
            <div class="flex items-center gap-2">
              <button @click="selectAllVisible" class="text-[11px] text-gray-500 hover:text-primary font-medium transition-colors">
                Tout sélectionner
              </button>
              <span class="text-gray-300">|</span>
              <button @click="clearSelection" class="text-[11px] text-gray-500 hover:text-red-500 font-medium transition-colors">
                Tout désélectionner
              </button>
            </div>
          </div>

          <!-- Chargement -->
          <div v-if="loadingProducts" class="flex flex-col items-center justify-center py-16">
            <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
            <p class="text-sm text-gray-400">Chargement des produits...</p>
          </div>

          <!-- État vide -->
          <div v-else-if="products.length === 0" class="flex flex-col items-center justify-center py-16 text-center px-6">
            <div class="text-gray-300 mb-3">
              <i class="fas fa-search text-5xl"></i>
            </div>
            <h3 class="text-base font-semibold text-gray-700 mb-1">Aucun produit trouvé</h3>
            <p class="text-sm text-gray-400 max-w-sm">Essayez de modifier vos critères de recherche</p>
          </div>

          <!-- Tableau des produits -->
          <div v-else>
            <table class="w-full">
              <thead>
                <tr class="bg-gray-50/80 border-b border-gray-200">
                  <th class="px-5 py-3 w-10"></th>
                  <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Produit</th>
                  <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Catégorie</th>
                  <th class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SKU</th>
                  <th class="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Stock</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="product in products" :key="product.id"
                  @click="toggleProduct(product.id)"
                  class="cursor-pointer transition-colors"
                  :class="selectedProducts.includes(product.id) ? 'bg-primary/[0.03]' : 'hover:bg-gray-50/80'">
                  <!-- Checkbox -->
                  <td class="px-5 py-3">
                    <div class="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
                      :class="selectedProducts.includes(product.id)
                        ? 'bg-primary border-primary'
                        : 'border-gray-300 hover:border-gray-400'">
                      <i v-if="selectedProducts.includes(product.id)" class="fas fa-check text-white text-[9px]"></i>
                    </div>
                  </td>

                  <!-- Nom -->
                  <td class="px-5 py-3">
                    <span class="text-sm font-medium text-gray-900">{{ product.name }}</span>
                  </td>

                  <!-- Catégorie -->
                  <td class="px-5 py-3">
                    <span v-if="product.category?.name"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                      {{ product.category.name }}
                    </span>
                    <span v-else class="text-xs text-gray-300">-</span>
                  </td>

                  <!-- SKU -->
                  <td class="px-5 py-3">
                    <span v-if="product.sku" class="text-xs text-gray-500 font-mono">{{ product.sku }}</span>
                    <span v-else class="text-xs text-gray-300">-</span>
                  </td>

                  <!-- Stock -->
                  <td class="px-5 py-3 text-center">
                    <span :class="[
                      'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold',
                      getStockBadgeClass(product)
                    ]">
                      {{ product.stock_quantity || 0 }} {{ product.unit || '' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Résumé -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-5">
          <div class="px-5 py-3 border-b bg-primary/5 border-primary/10">
            <h3 class="text-sm font-semibold text-primary">Résumé de l'inventaire</h3>
          </div>
          <div class="p-5 space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-box text-gray-400 text-[10px]"></i>
                </span>
                Produits sélectionnés
              </span>
              <span class="text-sm font-semibold text-gray-900">{{ selectedProducts.length }}</span>
            </div>
            <div class="h-px bg-gray-100"></div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400 flex items-center gap-2">
                <span class="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-cubes text-blue-500 text-[10px]"></i>
                </span>
                Stock théorique total
              </span>
              <span class="text-sm font-bold text-primary">{{ totalSelectedStock }}</span>
            </div>
          </div>

          <!-- Produits sélectionnés (aperçu) -->
          <div v-if="selectedProducts.length > 0" class="px-5 pb-3">
            <div class="max-h-[200px] overflow-y-auto space-y-1">
              <div v-for="product in selectedProductsList" :key="product.id"
                class="flex items-center justify-between px-2.5 py-1.5 bg-gray-50 rounded-lg">
                <span class="text-xs text-gray-700 truncate flex-1 mr-2">{{ product.name }}</span>
                <div class="flex items-center gap-2">
                  <span class="text-[10px] font-semibold text-gray-500">{{ product.stock_quantity || 0 }}</span>
                  <button @click.stop="toggleProduct(product.id)"
                    class="text-gray-400 hover:text-red-500 transition-colors">
                    <i class="fas fa-times text-[9px]"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="px-5 pb-5 pt-2">
            <button
              class="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              :disabled="isCreating || selectedProducts.length === 0"
              @click="showConfirmModal = true">
              <i class="fas fa-clipboard-check text-[10px]"></i>
              Créer l'inventaire
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de confirmation -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showConfirmModal"
          class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          @click.self="showConfirmModal = false">
          <div class="bg-white rounded-xl w-full max-w-3xl p-6 shadow-2xl">
            <!-- Header -->
            <div class="flex items-center gap-3 mb-5">
              <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <i class="fas fa-clipboard-check text-primary text-sm"></i>
              </div>
              <div>
                <h3 class="text-base font-bold text-gray-900">Confirmer l'inventaire ?</h3>
                <p class="text-[11px] text-gray-400">Vérifiez le résumé et assignez un responsable avant de valider</p>
              </div>
            </div>

            <!-- Contenu deux colonnes -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">

              <!-- Colonne gauche : Résumé -->
              <div class="bg-gray-50 rounded-lg p-4 space-y-2.5">
                <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Résumé</h4>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-400">Produits sélectionnés</span>
                  <span class="font-semibold text-gray-900">{{ selectedProducts.length }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-gray-400">Stock théorique total</span>
                  <span class="font-bold text-primary">{{ totalSelectedStock }} unité(s)</span>
                </div>
                <div class="h-px bg-gray-200 my-1"></div>

                <!-- Liste des produits -->
                <div class="max-h-[200px] overflow-y-auto space-y-1.5 pt-1">
                  <div v-for="product in selectedProductsList" :key="product.id"
                    class="flex items-center justify-between">
                    <span class="text-xs text-gray-600 truncate flex-1 mr-3">{{ product.name }}</span>
                    <span :class="[
                      'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold',
                      getStockBadgeClass(product)
                    ]">{{ product.stock_quantity || 0 }}</span>
                  </div>
                </div>
              </div>

              <!-- Colonne droite : Sélection utilisateur -->
              <div class="space-y-4">
                <div class="bg-gray-50 rounded-lg p-4">
                  <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Responsable de l'inventaire <span class="text-red-400">*</span>
                  </h4>

                  <!-- Recherche utilisateur -->
                  <div class="relative mb-3">
                    <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-[10px]"></i>
                    <input
                      v-model="userSearch"
                      type="text"
                      placeholder="Rechercher un utilisateur..."
                      class="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder-gray-300"
                    />
                  </div>

                  <!-- Liste des utilisateurs -->
                  <div v-if="loadingUsers" class="flex items-center justify-center py-6">
                    <span class="w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></span>
                  </div>
                  <div v-else class="max-h-[180px] overflow-y-auto space-y-1">
                    <button
                      v-for="user in filteredUsers"
                      :key="user.id"
                      @click="selectedUserId = user.id"
                      :class="[
                        'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all',
                        selectedUserId === user.id
                          ? 'bg-primary/10 border border-primary/30 ring-1 ring-primary/20'
                          : 'bg-white border border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                      ]">
                      <div :class="[
                        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold',
                        selectedUserId === user.id
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-500'
                      ]">
                        {{ getUserInitials(user) }}
                      </div>
                      <div class="flex-1 min-w-0">
                        <p :class="[
                          'text-xs font-medium truncate',
                          selectedUserId === user.id ? 'text-primary' : 'text-gray-700'
                        ]">{{ user.name }}</p>
                        <p class="text-[10px] text-gray-400 truncate">{{ user.email }}</p>
                      </div>
                      <i v-if="selectedUserId === user.id" class="fas fa-check-circle text-primary text-xs flex-shrink-0"></i>
                    </button>
                    <p v-if="filteredUsers.length === 0" class="text-xs text-gray-400 text-center py-4">
                      Aucun utilisateur trouvé
                    </p>
                  </div>
                </div>

                <!-- Notes -->
                <div class="bg-gray-50 rounded-lg p-4">
                  <h4 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Notes (optionnel)</h4>
                  <textarea
                    v-model="inventoryNotes"
                    rows="3"
                    placeholder="Ajouter des notes ou instructions..."
                    class="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 placeholder-gray-300 resize-none"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="flex gap-3">
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                @click="showConfirmModal = false">
                Annuler
              </button>
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isCreating || !selectedUserId"
                @click="confirmCreate">
                <span v-if="isCreating" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block mr-1"></span>
                <template v-else>
                  <i class="fas fa-check text-[10px] mr-1"></i>
                </template>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Modal de succès -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showSuccess"
          class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          @click.self="showSuccess = false">
          <div class="bg-white rounded-xl w-full max-w-sm p-6 text-center shadow-2xl">
            <div class="w-14 h-14 rounded-full bg-emerald-50 mx-auto mb-4 flex items-center justify-center">
              <i class="fas fa-check text-emerald-500 text-xl"></i>
            </div>
            <h3 class="text-base font-bold text-gray-900 mb-2">Inventaire créé !</h3>
            <p class="text-sm text-gray-500 mb-6">
              Référence : <strong class="text-gray-900">{{ createdInventory?.reference_number }}</strong>
            </p>
            <div class="flex gap-3">
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                @click="goToInventoryHistory">
                Voir les inventaires
              </button>
              <button
                class="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
                @click="startInventory">
                <i class="fas fa-play text-[10px] mr-1"></i>
                Commencer
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import categoriesService from '@/services/categoriesService';
import productsService from '@/services/productsService';
import physicalInventoryService from '@/services/physicalInventoryService';
import usersService from '@/services/usersService';
import { useToastStore } from '@/store/modules/toastStore';

export default {
    name: 'CreateInventory',
    setup() {
        const toastStore = useToastStore();
        return { toastStore };
    },
    data() {
        return {
            showCategorySelection: false,
            showConfirmModal: false,
            showSuccess: false,
            quickAction: '',

            categories: [],
            allCategories: [],
            selectedCategories: [],
            categorySearch: '',
            categorySearchTimeout: null,

            products: [],
            allProducts: [],
            selectedProducts: [],
            productSearch: '',
            productSearchTimeout: null,
            loadingProducts: false,
            stockFilter: '',
            selectedCategoryFilter: '',
            totalProducts: 0,

            users: [],
            selectedUserId: null,
            userSearch: '',
            loadingUsers: false,
            inventoryNotes: '',

            isCreating: false,
            createdInventory: null
        }
    },
    computed: {
        totalSelectedStock() {
            return this.allProducts
                .filter(p => this.selectedProducts.includes(p.id))
                .reduce((total, product) => total + (product.stock_quantity || 0), 0);
        },
        selectedProductsList() {
            return this.allProducts.filter(p => this.selectedProducts.includes(p.id));
        },
        filteredUsers() {
            if (!this.userSearch) return this.users;
            const search = this.userSearch.toLowerCase();
            return this.users.filter(u =>
                u.name?.toLowerCase().includes(search) ||
                u.email?.toLowerCase().includes(search)
            );
        }
    },
    async mounted() {
        await Promise.all([
            this.loadAllCategories(),
            this.loadProducts(),
            this.loadUsers()
        ]);
    },
    methods: {
        async loadUsers() {
            this.loadingUsers = true;
            try {
                const response = await usersService.getUsers({ per_page: 100 });
                this.users = response.data?.data || [];
            } catch (error) {
                console.error('Erreur chargement utilisateurs:', error);
            } finally {
                this.loadingUsers = false;
            }
        },

        getUserInitials(user) {
            if (!user?.name) return '?';
            return user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
        },

        async loadAllCategories() {
            try {
                const response = await categoriesService.getCategories({ having_products: 1 });
                this.allCategories = response.data?.data || [];
                this.categories = [...this.allCategories];
            } catch (error) {
                console.error('Erreur chargement catégories:', error);
            }
        },

        async loadProducts() {
            this.loadingProducts = true;
            try {
                const params = {
                    per_page: 100,
                    search: this.productSearch || undefined,
                    categories: this.selectedCategoryFilter ? [this.selectedCategoryFilter] : undefined
                };

                const response = await productsService.getProducts(params);
                let products = response.data?.data || [];

                if (this.stockFilter) {
                    products = products.filter(product => {
                        const qty = product.stock_quantity || 0;
                        switch (this.stockFilter) {
                            case 'in-stock': return qty > (product.minimum_stock || 5);
                            case 'low-stock': return qty > 0 && qty <= (product.minimum_stock || 5);
                            case 'out-of-stock': return qty === 0;
                            default: return true;
                        }
                    });
                }

                this.products = products;
                this.totalProducts = products.length;
                this.allProducts = [...products];
            } catch (error) {
                console.error('Erreur chargement produits:', error);
            } finally {
                this.loadingProducts = false;
            }
        },

        handleCategorySearch() {
            clearTimeout(this.categorySearchTimeout);
            this.categorySearchTimeout = setTimeout(() => {
                if (this.categorySearch) {
                    this.categories = this.allCategories.filter(cat =>
                        cat.name.toLowerCase().includes(this.categorySearch.toLowerCase())
                    );
                } else {
                    this.categories = [...this.allCategories];
                }
            }, 300);
        },

        handleProductSearch() {
            clearTimeout(this.productSearchTimeout);
            this.productSearchTimeout = setTimeout(() => {
                this.loadProducts();
            }, 300);
        },

        applyFilters() {
            this.loadProducts();
        },

        selectAllProducts() {
            this.quickAction = 'all';
            this.showCategorySelection = false;
            this.selectedProducts = [];
            this.stockFilter = '';
            this.selectedCategoryFilter = '';
            this.productSearch = '';
            this.loadProducts().then(() => {
                this.selectedProducts = this.products.map(p => p.id);
            });
        },

        selectByCategory() {
            this.quickAction = 'category';
            this.selectedCategories = [];
            this.showCategorySelection = true;
        },

        selectLowStock() {
            this.quickAction = 'low';
            this.showCategorySelection = false;
            this.selectedProducts = [];
            this.stockFilter = 'low-stock';
            this.loadProducts().then(() => {
                this.selectedProducts = this.products.map(p => p.id);
            });
        },

        toggleCategory(categoryId) {
            const index = this.selectedCategories.indexOf(categoryId);
            if (index > -1) {
                this.selectedCategories.splice(index, 1);
            } else {
                this.selectedCategories.push(categoryId);
            }
        },

        async loadProductsByCategories() {
            this.showCategorySelection = false;
            this.selectedCategoryFilter = this.selectedCategories[0] || '';
            await this.loadProducts();
            this.selectedProducts = this.products.map(p => p.id);
        },

        toggleProduct(productId) {
            const index = this.selectedProducts.indexOf(productId);
            if (index > -1) {
                this.selectedProducts.splice(index, 1);
            } else {
                this.selectedProducts.push(productId);
            }
        },

        selectAllVisible() {
            const visibleIds = this.products.map(p => p.id);
            visibleIds.forEach(id => {
                if (!this.selectedProducts.includes(id)) {
                    this.selectedProducts.push(id);
                }
            });
        },

        clearSelection() {
            this.selectedProducts = [];
            this.quickAction = '';
        },

        getStockBadgeClass(product) {
            const qty = product.stock_quantity || 0;
            if (qty === 0) return 'bg-red-50 text-red-600';
            if (qty <= (product.minimum_stock || 5)) return 'bg-amber-50 text-amber-700';
            return 'bg-emerald-50 text-emerald-700';
        },

        async confirmCreate() {
            await this.createInventory();
        },

        async createInventory() {
            if (this.selectedProducts.length === 0 || this.isCreating) return;

            this.isCreating = true;
            try {
                const payload = {
                    product_ids: this.selectedProducts,
                    user_id: this.selectedUserId
                };
                if (this.inventoryNotes) {
                    payload.notes = this.inventoryNotes;
                }
                const response = await physicalInventoryService.createInventory(payload);

                this.createdInventory = response.inventory;
                this.showConfirmModal = false;
                this.selectedUserId = null;
                this.inventoryNotes = '';
                this.userSearch = '';
                this.showSuccess = true;

                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire créé avec succès'
                });
            } catch (error) {
                console.error('Erreur création inventaire:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: error?.response?.data?.message || 'Erreur lors de la création'
                });
            } finally {
                this.isCreating = false;
            }
        },

        goToInventoryHistory() {
            this.$router.push({ name: 'inventory-history' });
        },

        startInventory() {
            if (this.createdInventory?.id) {
                this.$router.push({
                    name: 'inventory-count',
                    params: { id: this.createdInventory.id }
                });
            }
        }
    }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active > div,
.modal-leave-active > div {
  transition: transform 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from > div,
.modal-leave-to > div {
  transform: scale(0.95) translateY(-8px);
}
</style>
